import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Dashboard from "../../common/components/layout/Dashboard";
import { ToastProvider } from "../../common/components/ui/toast/toast.provider";
import Toast from "../../common/components/ui/toast";
import Loading from "../../common/components/ui/loading/Loading";
import {
  getPartnerChargeById,
  chargePartnerCharge,
  IPartnerChargeDetails,
} from "../../common/services/partner-charge.api";
import { useToastContext } from "../../common/components/ui/toast/toast.context";
import {
  FaArrowLeft,
  FaFileInvoiceDollar,
  FaMotorcycle,
  FaTools,
} from "react-icons/fa";

// Criando tipo para a referência mapeada que inclui 'itemTotal' e 'details'
type MappedReference = {
  type: string;
  id: string;
  itemTotal?: number;
  details?: {
    licensePlate?: string;
    createdAt?: string | Date;
    budgetMaintenance?: {
      corrective?: Array<{ quantity: number; name: string; sellPrice: number }>;
      preventive?: Array<{ quantity: number; name: string; sellPrice: number }>;
      misuse?: Array<{ quantity: number; name: string; sellPrice: number }>;
    };
  };
};

function PartnerChargeDetailsContent() {
  const { chargeId } = useParams();
  const navigate = useNavigate();
  const { notifyError, notifySuccess } = useToastContext();
  const [charge, setCharge] = useState<IPartnerChargeDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      if (!chargeId) return;
      const res = await getPartnerChargeById(chargeId);
      if (res.ok) {
        // Cast para satisfazer a interface
        setCharge(res.data as unknown as IPartnerChargeDetails);
      } else {
        notifyError("Erro ao carregar detalhes da cobrança.");
      }
      setIsLoading(false);
    }
    loadData();
  }, [chargeId, notifyError]);

  const handleGenerateNF = async () => {
    if (!chargeId) return;

    try {
      const result = await chargePartnerCharge(chargeId);

      if (result.ok) {
        notifySuccess(
          "Cobrança gerada com sucesso! Status atualizado para CHARGED.",
        );

        setCharge((prevCharge) => {
          if (!prevCharge) return null;
          return {
            ...prevCharge,
            status: "CHARGED",
          };
        });
      } else {
        notifyError(result.message || "Erro ao alterar status da cobrança.");
      }
    } catch (error) {
      notifyError("Erro inesperado ao gerar a NF.");
    }
  };

  if (isLoading) return <Loading />;
  if (!charge) return <div className="p-6">Cobrança não encontrada.</div>;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Botão Voltar */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center text-bluePrimary hover:underline mb-6 font-medium"
      >
        <FaArrowLeft className="mr-2" /> Voltar
      </button>

      {/* Header Bonito (Resumo Geral) */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-8 border border-gray-100 flex flex-col md:flex-row justify-between items-start md:items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            {charge.partnerCompanyName}
            <span
              className={`text-xs px-2 py-1 rounded-full text-white ${charge.status === "OPEN" ? "bg-yellow-500" : "bg-green-500"}`}
            >
              {charge.status === "OPEN" ? "Em Aberto" : "Cobrado"}
            </span>
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Data de Abertura:{" "}
            {new Date(charge.createdAt).toLocaleDateString("pt-BR")}
          </p>

          <div className="flex gap-6 mt-4">
            <div className="flex items-center text-gray-600">
              <FaMotorcycle className="mr-2 text-blue-500 text-lg" />
              <span>
                <strong className="text-gray-900">
                  {charge.summary?.totalVehicles || 0}
                </strong>{" "}
                Veículos
              </span>
            </div>
            <div className="flex items-center text-gray-600">
              <FaTools className="mr-2 text-orange-500 text-lg" />
              <span>
                <strong className="text-gray-900">
                  {charge.summary?.totalServices || 0}
                </strong>{" "}
                Serviços Realizados
              </span>
            </div>
          </div>
        </div>

        <div className="mt-6 md:mt-0 flex flex-col items-end">
          <p className="text-sm text-gray-500 mb-1">Valor Total</p>
          <p className="text-3xl font-extrabold text-green-600 mb-4">
            {new Intl.NumberFormat("pt-BR", {
              style: "currency",
              currency: "BRL",
            }).format(charge.summary?.totalValue || 0)}
          </p>
          <button
            onClick={handleGenerateNF}
            disabled={charge.status === "CHARGED"}
            className="flex items-center bg-bluePrimary hover:bg-blue-700 text-white font-bold py-2 px-6 rounded shadow disabled:opacity-50 transition-colors"
          >
            <FaFileInvoiceDollar className="mr-2" /> Gerar NF
          </button>
        </div>
      </div>

      {/* Listagem das Manutenções / Preparações */}
      <h2 className="text-xl font-bold text-gray-800 mb-4 border-b pb-2">
        Detalhes dos Serviços
      </h2>

      <div className="space-y-4">
        {(charge.detailedReferences as unknown as MappedReference[])?.map(
          (ref, index: number) => (
            <div
              key={index}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
            >
              {/* Cabeçalho do Card */}
              <div className="bg-gray-100 px-4 py-3 flex justify-between items-center border-b">
                <div>
                  <span className="font-bold text-gray-800 uppercase text-sm tracking-wide">
                    {ref.type} - Placa:{" "}
                    <span className="text-bluePrimary">
                      {ref.details?.licensePlate || "N/A"}
                    </span>
                  </span>
                  <p className="text-xs text-gray-500 mt-1">
                    Executado em:{" "}
                    {ref.details?.createdAt
                      ? new Date(ref.details.createdAt).toLocaleString("pt-BR")
                      : "N/A"}
                  </p>
                </div>
                <div className="font-bold text-gray-800">
                  {new Intl.NumberFormat("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                  }).format(ref.itemTotal || 0)}
                </div>
              </div>

              {/* Itens do Serviço */}
              <div className="p-4">
                {ref.details?.budgetMaintenance ? (
                  <ul className="divide-y divide-gray-100">
                    {(["corrective", "preventive", "misuse"] as const).map(
                      (typeLabel) =>
                        ref.details?.budgetMaintenance?.[typeLabel]?.map(
                          (piece, pIndex: number) => (
                            <li
                              key={`${typeLabel}-${pIndex}`}
                              className="py-2 flex justify-between text-sm"
                            >
                              <span className="text-gray-600">
                                <span className="font-medium">
                                  {piece.quantity}x
                                </span>{" "}
                                {piece.name}{" "}
                                <span className="text-xs text-gray-400">
                                  ({typeLabel})
                                </span>
                              </span>
                              <span className="text-gray-800 font-medium">
                                {new Intl.NumberFormat("pt-BR", {
                                  style: "currency",
                                  currency: "BRL",
                                }).format(
                                  (piece.quantity || 1) *
                                    (piece.sellPrice || 0),
                                )}
                              </span>
                            </li>
                          ),
                        ),
                    )}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500 italic">
                    Nenhum item detalhado disponível.
                  </p>
                )}
              </div>
            </div>
          ),
        )}

        {charge.detailedReferences?.length === 0 && (
          <p className="text-gray-500 text-center py-8 bg-white rounded border border-dashed">
            Nenhum serviço atrelado a esta cobrança.
          </p>
        )}
      </div>
    </div>
  );
}

export default function PartnerChargeDetailsPage() {
  return (
    <ToastProvider>
      <Dashboard>
        <PartnerChargeDetailsContent />
      </Dashboard>
      <Toast />
    </ToastProvider>
  );
}
